<script lang="ts">
import "@cisco-u/avatar/cu-avatar.js";

import { defineComponent } from "vue";
import { mapGetters, mapMutations } from "vuex";

import MarqueeCardDay0 from "@/layout/Header/MarqueeCard.vue";
import PageHeader from "@/layout/Header/PageHeader.vue";
import dateFormats from "@/utils/dateFormats";
import { canShowGenAIModal } from "@/utils/genAIConsent";

export default defineComponent({
  emits: ["close", "open", "update-query"],
  components: {
    MarqueeCardDay0,
    PageHeader,
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      showGoal: false,
    };
  },
  computed: {
    ...mapGetters({
      userStatus: "user/status",
      announcementBanner: "commerce/announcementBanner",
    }),
    initials() {
      return this.userStatus?.userInitials ?? "";
    },
    userDisplayName() {
      return this.userStatus?.display_name ?? "";
    },
    showPageHeader() {
      return this.$route?.meta?.pageHeader === true;
    },
    hideMarquee() {
      return this.$route?.meta?.hideMarquee === true;
    },
    showProfileHeader() {
      return this.$route?.meta?.showProfileHeader === true;
    },
    isPathRoute() {
      return this.$route?.name === "Path";
    },
    getUser() {
      if (!this.userStatus) return false;

      this.start();
      return true;
    },
    isPastUtc(): boolean {
      if (this.announcementBanner?.endDate) {
        return dateFormats.isPastUtc(this.announcementBanner.endDate);
      }
      return true;
    },
  },

  methods: {
    ...mapMutations({
      setupToggleShowAnyway: "user/setupToggleShowAnyway",
    }),
    closeModal() {
      this.$emit("close");
      this.setupToggleShowAnyway(false);
    },
    openModal() {
      this.$emit("open");
      this.setupToggleShowAnyway(true);
    },
    start() {
      if (
        !this.isGenAIConsent() &&
        this.userStatus &&
        this.$route?.name == "ForYou" &&
        !this.userStatus.surveyCompleted
      ) {
        this.openModal();
      }
    },
    updateQuery(newValue: any) {
      this.$emit("update-query", newValue);
    },
    isGenAIConsent() {
      return this.userStatus && canShowGenAIModal(this.userStatus);
    },
  },
});
</script>

<template>
  <div>
    <div
      id="profile"
      :class="[
        {
          'min-h-[42rem] lg:min-h-[46rem] xl:min-h-[30rem]':
            (showPageHeader && !hideMarquee) ||
            (!showPageHeader && !hideMarquee && !isPastUtc),
        },
        {
          'min-h-[45.625rem] lg:min-h-[49.625rem] xl:min-h-[33.625rem]':
            !hideMarquee && isPastUtc,
        },
      ]"
    >
      <page-header
        :dark="true"
        :scrolled="true"
        :show-logo="true"
        instance="profile-header"
        :searchQuery="searchQuery"
        @update-query="updateQuery"
      />

      <div
        v-show="!hideMarquee"
        :class="[
          'mx-auto flex w-full justify-center pt-24 text-white md:fixed md:top-[8.625rem] md:justify-between md:pt-0 lg:top-[12.625rem]',
          {
            'md:left-0 md:right-0 md:top-20 md:pb-0 lg:top-36': showPageHeader,
          },
        ]"
      >
        <div :class="['container mx-auto flex justify-center gap-4 md:px-4']">
          <Marquee-Card-Day0
            :status="userStatus"
            @openGoSurveyModal="openModal"
            v-show="getUser"
          />
        </div>
      </div>
    </div>

    <div
      v-if="showProfileHeader"
      :class="[
        'mx-4 md:mb-[5.625rem] flex flex-row pl-4 md:container md:mx-auto',
        {
          'mt-56': hideMarquee,
          'mt-12': !hideMarquee,
        },
      ]"
    >
      <div>
        <cu-avatar
          cisco_two="true"
          :letters="initials"
          width="6rem"
          height="8.0625rem"
          fontSize="2.8125rem"
          class="pointer-events-none"
        ></cu-avatar>
      </div>
      <div class="flex flex-col pl-11">
        <p class="text-xl md:text-3xl text-white">Welcome back,</p>
        <p class="text-3xl md:text-7xl leading-[1.3] text-white">
          {{ userDisplayName }}
        </p>
      </div>
    </div>
  </div>
</template>
